import { format } from 'date-fns';

/**
 * Calendar Date Utilities
 *
 * These utilities handle dates that represent calendar days (without meaningful time components).
 * All calendar dates should be stored at noon UTC (12:00:00.000Z) to prevent timezone-related
 * day shifts when displaying dates across different timezones.
 *
 * Why noon UTC?
 * - Noon UTC is +/- 12 hours from any timezone in the world
 * - This prevents calendar day boundary crossing in any timezone
 * - A date at noon UTC will display as the same calendar day everywhere
 *
 * Example problem this solves:
 * - User in Denver (UTC-7) selects January 12th at midnight local time
 * - Without normalization: stored as 2025-01-12T07:00:00.000Z
 * - User in Louisiana (UTC-6) views it and might see a different day
 * - With noon UTC: stored as 2025-01-12T12:00:00.000Z - consistent everywhere
 */

/**
 * Converts a calendar date to noon UTC timestamp.
 * Use this when storing dates that represent a calendar day (no time component).
 *
 * @param date - A Date object representing a calendar date
 * @returns Milliseconds since epoch for that date at noon UTC
 *
 * @example
 * // User selects January 12, 2025 in their calendar picker
 * const selectedDate = new Date(2025, 0, 12); // Local time
 * const timestamp = toNoonUTC(selectedDate);
 * // Always returns timestamp for 2025-01-12T12:00:00.000Z
 */
export function toNoonUTC(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
}

/**
 * Placeholder rendered in place of a date that is missing or unparseable.
 */
export const MISSING_DATE_PLACEHOLDER = '—';

/**
 * Coerces an untrusted value into a valid Date, or null when it cannot represent one.
 *
 * Guards two distinct failure modes we have hit in production:
 * - `undefined` / `''` / garbage strings produce an Invalid Date, and date-fns
 *   `format()` throws `RangeError: Invalid time value` on those.
 * - `null` is coerced by `new Date(null)` to the Unix epoch, which silently renders
 *   as "Jan 1st 1970" instead of signalling that the value was absent.
 *
 * @param timestamp - Untrusted Date, timestamp (number), or ISO string
 * @returns A valid Date, or null if the input cannot represent one
 */
export function toValidDate(timestamp: Date | number | string | null | undefined): Date | null {
  if (timestamp === null || timestamp === undefined || timestamp === '') {
    return null;
  }

  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Creates a Date object from a stored noon-UTC timestamp.
 * Extracts the calendar date without timezone shifts.
 *
 * @param timestamp - A Date, timestamp (number), or ISO string stored at noon UTC
 * @returns Date object with the correct calendar date at noon local time
 *
 * @example
 * // Date stored in MongoDB
 * const storedDate = order.dropOffWindow.day; // 2025-01-12T12:00:00.000Z
 * const localDate = fromNoonUTC(storedDate);
 * // Returns Date object representing January 12, 2025 at noon local time
 */
export function fromNoonUTC(timestamp: Date | number | string): Date {
  const d = new Date(timestamp);
  // Extract UTC components to avoid local timezone interpretation
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0);
}

/**
 * Formats a stored noon-UTC date for display.
 * Uses UTC date components to ensure consistent display across timezones.
 *
 * @param timestamp - A Date, timestamp (number), or ISO string stored at noon UTC
 * @param formatStr - date-fns format string (e.g., 'EEE MMM do yyyy')
 * @returns Formatted date string
 *
 * @example
 * // Display order drop-off date
 * const display = formatCalendarDate(order.dropOffWindow.day, 'EEE MMM do yyyy');
 * // Returns "Sun Jan 12th 2025" regardless of user's timezone
 *
 * @example
 * // Different format
 * const shortDisplay = formatCalendarDate(order.dropOffWindow.day, 'MM/dd/yyyy');
 * // Returns "01/12/2025"
 */
export function formatCalendarDate(
  timestamp: Date | number | string | null | undefined,
  formatStr: string = 'EEE MMM do yyyy',
  fallback: string = MISSING_DATE_PLACEHOLDER
): string {
  const d = toValidDate(timestamp);
  if (!d) {
    return fallback;
  }
  // Create a date using UTC components to prevent timezone shifts
  // We use the UTC date components but create a local Date for formatting
  const localDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return format(localDate, formatStr);
}

/**
 * Formats a timestamp that has a meaningful time component (not a calendar day).
 * Unlike `formatCalendarDate`, this does not strip the time via UTC components.
 *
 * Use this instead of a bare `format(new Date(x), ...)` anywhere the input can be
 * absent or malformed — date-fns throws `RangeError: Invalid time value` on an
 * Invalid Date, which takes down the whole React tree.
 *
 * @param timestamp - A Date, timestamp (number), or ISO string; may be null/undefined
 * @param formatStr - date-fns format string
 * @param fallback - Returned when the input is absent or unparseable
 * @returns Formatted date-time string, or `fallback`
 */
export function formatDateTime(
  timestamp: Date | number | string | null | undefined,
  formatStr: string = "MMM d, yyyy 'at' p",
  fallback: string = MISSING_DATE_PLACEHOLDER
): string {
  const d = toValidDate(timestamp);
  return d ? format(d, formatStr) : fallback;
}

/**
 * Checks if a date is already normalized to noon UTC.
 * Useful for validation and debugging.
 *
 * @param date - A Date object to check
 * @returns true if the date is at exactly noon UTC (12:00:00.000Z)
 */
export function isNoonUTC(date: Date): boolean {
  return (
    date.getUTCHours() === 12 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

/**
 * Normalizes a date to noon UTC, returning null if already normalized.
 * Used primarily in migrations to avoid unnecessary updates.
 *
 * @param date - A Date object to normalize
 * @returns New Date at noon UTC, or null if already normalized or invalid
 */
export function normalizeToNoonUTC(date: Date): Date | null {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  if (isNoonUTC(date)) {
    return null;
  }

  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12, 0, 0, 0)
  );
}
